import React from "react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CornerTicks, Backdrop } from "@/components/IntersectMotif";
import { FinalCTA } from "@/pages/Home";
import { IMG, VALUES } from "@/lib/content";
import { useLang } from "@/lib/i18n";

const Block = ({ eyebrow, title, children, dark, image }) => {
  const { t } = useLang();
  return (
    <section className={`section-pad relative overflow-hidden ${dark ? "bg-charcoal" : ""}`}>
      {image && <Backdrop src={image} opacity={0.1} />}
      <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal><p className="eyebrow mb-6">{t(eyebrow)}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory">{t(title)}</h2></Reveal>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-lg leading-relaxed text-soft-stone">
          <Reveal delay={0.15}>{children}</Reveal>
        </div>
      </div>
    </section>
  );
};

export default function About() {
  const { t } = useLang();
  return (
    <>
      <PageHero eyebrow="About" title="Building value through capital, relationships and execution." image={IMG.boardroomView} />
      <Block eyebrow="Who We Are" title="A Saudi investment and strategic holding company." image={IMG.kafdAerial}>
        <p>{t("Intersect Holding develops ventures, partnerships and long-term commercial relationships across Saudi Arabia and international markets. We are positioned as an investor, partner, venture builder and originator of opportunity.")}</p>
        <p>{t("We are not a consultancy. We participate, build and hold — creating value where capital, markets, relationships and ideas intersect.")}</p>
      </Block>
      <Block eyebrow="Our Purpose" title="Identifying where value can be created." dark>
        <p className="serif-italic text-2xl md:text-3xl text-luxury-gold">{t("Intersect exists to identify points where capital, markets, relationships and ideas can intersect to create new economic value.")}</p>
      </Block>
      <Block eyebrow="Our Philosophy" title="More than capital." image={IMG.skylineSunset}>
        <p>{t("We believe the most valuable companies are built at intersections — where capital meets opportunity, where global capability meets Saudi ambition, and where relationships become partnerships.")}</p>
        <p>{t("Our approach is patient and long-term. We seek durable businesses rather than short-term transactions.")}</p>
      </Block>

      <section className="section-pad bg-charcoal relative overflow-hidden">
        <Backdrop src={IMG.madinah} opacity={0.1} />
        <div className="container-x relative z-10">
          <Reveal><p className="eyebrow mb-6">{t("Our Values")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="text-2xl md:text-4xl font-light tracking-tight text-ivory mb-14">{t("The principles that guide us.")}</h2></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-primary-gold/10 border border-primary-gold/10">
            {VALUES.map((v, i) => (
              <Reveal key={v}>
                <div className="card-lift bg-charcoal p-10 min-h-[180px] flex flex-col justify-between hover:bg-obsidian transition-colors duration-300" data-testid={`value-${i}`}>
                  <span className="text-primary-gold text-xs tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="text-2xl md:text-3xl font-light tracking-tight text-ivory">{t(v)}</h3>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.cityNight} opacity={0.12} />
        <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal><p className="eyebrow mb-6">{t("Saudi Roots. Global Perspective.")}</p></Reveal>
            <Reveal delay={0.1}><h2 className="text-3xl md:text-5xl font-light tracking-tight text-ivory">{t("Grounded in Saudi Arabia, connected to the world.")}</h2></Reveal>
            <Reveal delay={0.2}><p className="mt-8 text-lg leading-relaxed text-soft-stone max-w-xl">{t("Our network spans investors, founders, corporations, family offices and institutions — the relationships that turn opportunities into companies.")}</p></Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.2}>
              <div className="relative">
                <div className="absolute -inset-3 border border-primary-gold/15" />
                <img src={IMG.kafdTower} alt="" className="relative w-full aspect-[4/3] object-cover" />
                <CornerTicks />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
